"use client";

import { HAND_CURSOR_CSS } from "@/lib/ui/cursor";

// Dernier recours : erreur DANS la mise en page racine elle-même.
//
// Ce fichier remplace `app/layout.tsx`, il doit donc rendre `<html>` et
// `<body>` lui-même. Conséquence à ne pas oublier : la feuille de style globale
// importée par la mise en page racine n'est PAS chargée ici. Aucune classe
// Tailwind ne s'appliquerait — d'où les styles en ligne, qui ne sont pas un
// relâchement mais la seule chose qui fonctionne à cet endroit.
//
// Règle de survie : rester le plus pauvre possible en dépendances. Une
// frontière d'erreur qui lève elle-même laisse un écran blanc, ce qui est pire
// que ce qu'elle répare. Seul Sentry est importé, parce qu'un plantage de la
// racine est précisément l'incident qu'il ne faut pas rater.

import * as React from "react";
import * as Sentry from "@sentry/nextjs";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  React.useEffect(() => {
    // Ici on remonte TOUJOURS, `digest` ou non : un échec de la mise en page
    // racine est trop grave pour être filtré par l'heuristique utilisée dans
    // les frontières plus profondes.
    Sentry.captureException(error);
  }, [error]);

  return (
    <html lang="fr">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1.5rem",
          background: "#f7f8fb",
          fontFamily:
            "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
          color: "#0f172a",
        }}
      >
        <div
          style={{
            maxWidth: "26rem",
            width: "100%",
            textAlign: "center",
            background: "#ffffff",
            border: "1px solid #e8eaf0",
            borderRadius: "1rem",
            padding: "1.75rem",
          }}
        >
          <h1 style={{ margin: 0, fontSize: "1.125rem", fontWeight: 600 }}>
            OnePlace n&apos;a pas pu démarrer
          </h1>
          <p
            style={{
              margin: "0.75rem 0 0",
              fontSize: "0.875rem",
              lineHeight: 1.55,
              color: "#64748b",
            }}
          >
            Une erreur est survenue au chargement de l&apos;application. Nous en avons
            été prévenus. Réessayez dans un instant — si le problème persiste,
            citez-nous la référence ci-dessous.
          </p>

          <button
            type="button"
            onClick={reset}
            style={{
              marginTop: "1.25rem",
              padding: "0.5rem 1.1rem",
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#ffffff",
              background: "#5079ff",
              border: "none",
              borderRadius: "0.75rem",
              cursor: HAND_CURSOR_CSS,
            }}
          >
            Réessayer
          </button>

          {error.digest ? (
            <p style={{ margin: "1.25rem 0 0", fontSize: "0.75rem", color: "#94a3b8" }}>
              Référence : <span style={{ fontFamily: "ui-monospace, monospace" }}>{error.digest}</span>
            </p>
          ) : null}
        </div>
      </body>
    </html>
  );
}
